import React, { createContext, useContext, useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);     // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { setUser(false); setLoading(false); return; }
      const res = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        localStorage.removeItem('auth_token');
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { checkAuth(); }, []);

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Server error. Please try again.");
      }
      if (!res.ok) throw new Error(formatError(data.detail));
      localStorage.setItem('auth_token', data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return data;
    } catch (e) {
      if (e.message.includes('body') || e.message.includes('clone') || e.message.includes('stream')) {
        throw new Error("Invalid email or password");
      }
      throw e;
    }
  };

  const register = async (email, password, name) => {
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Server error. Please try again.");
      }
      if (!res.ok) throw new Error(formatError(data.detail));
      localStorage.setItem('auth_token', data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return data;
    } catch (e) {
      if (e.message.includes('body') || e.message.includes('clone') || e.message.includes('stream')) {
        throw new Error("Registration failed. Please try again.");
      }
      throw e;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    localStorage.removeItem('auth_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

function formatError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}
